import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import {
  User,
  School,
  MapPin,
  Calendar,
  Ruler,
  Scale,
  ChevronRight,
  ChevronLeft,
  Check,
  Sparkles,
  Target,
  Trophy,
  BadgeCheck,
  Camera,
  Link as LinkIcon
} from "lucide-react";
import { NSBLogo } from "../components/NSBLogo";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Position options
const POSITIONS = [
  { value: "SS", label: "Shortstop (SS)" },
  { value: "2B", label: "Second Base (2B)" },
  { value: "3B", label: "Third Base (3B)" },
  { value: "1B", label: "First Base (1B)" },
  { value: "C", label: "Catcher (C)" },
  { value: "P", label: "Pitcher (P)" },
  { value: "CF", label: "Center Field (CF)" },
  { value: "LF", label: "Left Field (LF)" },
  { value: "RF", label: "Right Field (RF)" },
  { value: "OF", label: "Outfield (OF)" },
  { value: "IF", label: "Infield (IF)" },
  { value: "UTIL", label: "Utility (UTIL)" }
];

// Height options
const HEIGHTS = [
  "5'4\"", "5'5\"", "5'6\"", "5'7\"", "5'8\"", "5'9\"", "5'10\"", "5'11\"",
  "6'0\"", "6'1\"", "6'2\"", "6'3\"", "6'4\"", "6'5\"", "6'6\""
];

// Graduation years
const currentYear = new Date().getFullYear();
const GRAD_YEARS = Array.from({ length: 8 }, (_, i) => currentYear + i);

// Competition levels
const COMPETITION_LEVELS = [
  { value: "youth", label: "Youth (12U)" },
  { value: "middle_school", label: "Middle School" },
  { value: "travel", label: "Travel Ball" },
  { value: "high_school", label: "High School" },
  { value: "prep", label: "Prep School" },
  { value: "juco", label: "JUCO" },
  { value: "d1", label: "Division I" },
  { value: "d2", label: "Division II" },
  { value: "d3", label: "Division III" },
  { value: "naia", label: "NAIA" }
];

// Step indicator component
const StepIndicator = ({ currentStep, totalSteps }) => (
  <div className="flex items-center justify-center gap-2 mb-8">
    {Array.from({ length: totalSteps }, (_, i) => (
      <div
        key={i}
        className={`h-2 rounded-full transition-all duration-300 ${
          i < currentStep 
            ? "w-8 bg-blue-800" 
            : i === currentStep 
              ? "w-8 bg-blue-800 animate-pulse" 
              : "w-2 bg-white/20"
        }`}
      />
    ))}
  </div>
);

export default function CreateProfilePage() {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    // Basic Info (Step 1)
    name: "",
    position_primary: "",
    position_secondary: "",
    bats: "",
    throws: "",
    
    // Physical & School (Step 2)
    height: "",
    weight: "",
    school: "",
    location: "",
    graduation_year: "",
    competition_level: "",
    
    // Academic & Contact (Step 3)
    gpa: "",
    act_score: "",
    sat_score: "",
    bio: "",
    
    // Social & Links (Step 4)
    twitter: "",
    instagram: "",
    highlight_video_url: "",
    allow_contact: true
  });

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const validateStep = (stepIndex) => {
    switch (stepIndex) {
      case 0:
        if (!formData.name.trim()) {
          toast.error("Please enter your name");
          return false;
        }
        if (!formData.position_primary) {
          toast.error("Please select your primary position");
          return false;
        }
        return true;
      case 1:
        if (!formData.graduation_year) {
          toast.error("Please select your graduation year");
          return false;
        }
        return true;
      case 2:
        return true; // Academic is optional
      case 3:
        return true; // Social is optional
      default:
        return true;
    }
  };

  const nextStep = () => {
    if (validateStep(step)) {
      setStep(prev => Math.min(prev + 1, 3));
    }
  };

  const prevStep = () => {
    setStep(prev => Math.max(prev - 1, 0));
  };

  const handleSubmit = async () => {
    setLoading(true);
    
    try {
      const token = localStorage.getItem("token");
      
      // Build the profile data
      const profileData = {
        name: formData.name,
        position_primary: formData.position_primary,
        position_secondary: formData.position_secondary || null,
        bats: formData.bats || null,
        throws: formData.throws || null,
        height: formData.height || null,
        weight: formData.weight ? parseInt(formData.weight) : null,
        school: formData.school || null,
        location: formData.location || null,
        graduation_year: formData.graduation_year ? parseInt(formData.graduation_year) : null,
        competition_level: formData.competition_level || null,
        gpa: formData.gpa ? parseFloat(formData.gpa) : null,
        act_score: formData.act_score ? parseInt(formData.act_score) : null,
        sat_score: formData.sat_score ? parseInt(formData.sat_score) : null,
        bio: formData.bio || null,
        highlight_video_url: formData.highlight_video_url || null,
        social_links: {
          twitter: formData.twitter || null,
          instagram: formData.instagram || null
        },
        allow_contact: formData.allow_contact,
        is_public: true
      };

      const response = await fetch(`${API_URL}/api/players/profile`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {})
        },
        credentials: "include",
        body: JSON.stringify(profileData)
      });

      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.detail || "Failed to create profile");
      }

      const data = await response.json();
      
      toast.success("Profile created successfully! 🎉");
      
      // Redirect to athlete dashboard
      navigate("/dashboard/athlete", { replace: true });
    } catch (error) {
      console.error("Profile creation error:", error);
      toast.error(error.message || "Failed to create profile");
    } finally {
      setLoading(false);
    }
  };

  const steps = [
    {
      title: "Basic Info",
      subtitle: "Tell us about yourself",
      icon: User,
      content: (
        <div className="space-y-6">
          <div>
            <Label className="text-white/70 mb-2 block">Full Name *</Label>
            <Input
              value={formData.name}
              onChange={(e) => handleChange("name", e.target.value)}
              placeholder="Enter your full name"
              className="bg-white/5 border-white/10 text-white placeholder:text-white/30"
            />
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-white/70 mb-2 block">Primary Position *</Label>
              <Select value={formData.position_primary} onValueChange={(v) => handleChange("position_primary", v)}>
                <SelectTrigger className="bg-white/5 border-white/10 text-white">
                  <SelectValue placeholder="Select position" />
                </SelectTrigger>
                <SelectContent>
                  {POSITIONS.map(pos => (
                    <SelectItem key={pos.value} value={pos.value}>{pos.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-white/70 mb-2 block">Secondary Position</Label>
              <Select value={formData.position_secondary} onValueChange={(v) => handleChange("position_secondary", v)}>
                <SelectTrigger className="bg-white/5 border-white/10 text-white">
                  <SelectValue placeholder="Optional" />
                </SelectTrigger>
                <SelectContent>
                  {POSITIONS.map(pos => (
                    <SelectItem key={pos.value} value={pos.value}>{pos.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-white/70 mb-2 block">Bats</Label>
              <Select value={formData.bats} onValueChange={(v) => handleChange("bats", v)}>
                <SelectTrigger className="bg-white/5 border-white/10 text-white">
                  <SelectValue placeholder="Select" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="R">Right</SelectItem>
                  <SelectItem value="L">Left</SelectItem>
                  <SelectItem value="S">Switch</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-white/70 mb-2 block">Throws</Label>
              <Select value={formData.throws} onValueChange={(v) => handleChange("throws", v)}>
                <SelectTrigger className="bg-white/5 border-white/10 text-white">
                  <SelectValue placeholder="Select" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="R">Right</SelectItem>
                  <SelectItem value="L">Left</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
      )
    },
    {
      title: "Physical & School",
      subtitle: "Where you play",
      icon: School,
      content: (
        <div className="space-y-6">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-white/70 mb-2 block">Height</Label>
              <Select value={formData.height} onValueChange={(v) => handleChange("height", v)}>
                <SelectTrigger className="bg-white/5 border-white/10 text-white">
                  <SelectValue placeholder="Select height" />
                </SelectTrigger>
                <SelectContent>
                  {HEIGHTS.map(h => (
                    <SelectItem key={h} value={h}>{h}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-white/70 mb-2 block">Weight (lbs)</Label>
              <Input
                type="number"
                value={formData.weight}
                onChange={(e) => handleChange("weight", e.target.value)}
                placeholder="e.g. 175"
                className="bg-white/5 border-white/10 text-white placeholder:text-white/30"
              />
            </div>
          </div>

          <div>
            <Label className="text-white/70 mb-2 block">School</Label>
            <Input
              value={formData.school}
              onChange={(e) => handleChange("school", e.target.value)}
              placeholder="Enter your school name"
              className="bg-white/5 border-white/10 text-white placeholder:text-white/30"
            />
          </div>

          <div>
            <Label className="text-white/70 mb-2 block">Location</Label>
            <Input
              value={formData.location}
              onChange={(e) => handleChange("location", e.target.value)}
              placeholder="City, State"
              className="bg-white/5 border-white/10 text-white placeholder:text-white/30"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-white/70 mb-2 block">Graduation Year *</Label>
              <Select value={formData.graduation_year} onValueChange={(v) => handleChange("graduation_year", v)}>
                <SelectTrigger className="bg-white/5 border-white/10 text-white">
                  <SelectValue placeholder="Select year" />
                </SelectTrigger>
                <SelectContent>
                  {GRAD_YEARS.map(y => (
                    <SelectItem key={y} value={y.toString()}>{y}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-white/70 mb-2 block">Competition Level</Label>
              <Select value={formData.competition_level} onValueChange={(v) => handleChange("competition_level", v)}>
                <SelectTrigger className="bg-white/5 border-white/10 text-white">
                  <SelectValue placeholder="Select level" />
                </SelectTrigger>
                <SelectContent>
                  {COMPETITION_LEVELS.map(l => (
                    <SelectItem key={l.value} value={l.value}>{l.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
      )
    },
    {
      title: "Academic",
      subtitle: "Your academic profile",
      icon: Target,
      content: (
        <div className="space-y-6">
          <div className="grid grid-cols-3 gap-4">
            <div>
              <Label className="text-white/70 mb-2 block">GPA</Label>
              <Input
                type="number"
                step="0.01"
                min="0"
                max="5"
                value={formData.gpa}
                onChange={(e) => handleChange("gpa", e.target.value)}
                placeholder="e.g. 3.5"
                className="bg-white/5 border-white/10 text-white placeholder:text-white/30"
              />
            </div>
            <div>
              <Label className="text-white/70 mb-2 block">ACT Score</Label>
              <Input
                type="number"
                min="1"
                max="36"
                value={formData.act_score}
                onChange={(e) => handleChange("act_score", e.target.value)}
                placeholder="1-36"
                className="bg-white/5 border-white/10 text-white placeholder:text-white/30"
              />
            </div>
            <div>
              <Label className="text-white/70 mb-2 block">SAT Score</Label>
              <Input
                type="number"
                min="400"
                max="1600"
                value={formData.sat_score}
                onChange={(e) => handleChange("sat_score", e.target.value)}
                placeholder="400-1600"
                className="bg-white/5 border-white/10 text-white placeholder:text-white/30"
              />
            </div>
          </div>

          <div>
            <Label className="text-white/70 mb-2 block">Bio</Label>
            <Textarea
              value={formData.bio}
              onChange={(e) => handleChange("bio", e.target.value)}
              placeholder="Tell coaches about yourself, your goals, and what makes you stand out..."
              rows={4}
              className="bg-white/5 border-white/10 text-white placeholder:text-white/30 resize-none"
            />
          </div>

          <div className="bg-blue-500/10 border border-blue-500/20 rounded-xl p-4">
            <p className="text-blue-400 text-sm">
              💡 Academic info helps coaches understand your eligibility and scholarship potential.
            </p>
          </div>
        </div>
      )
    },
    {
      title: "Social & Links",
      subtitle: "Connect your profiles",
      icon: LinkIcon,
      content: (
        <div className="space-y-6">
          <div>
            <Label className="text-white/70 mb-2 block">Twitter/X Profile</Label>
            <Input
              value={formData.twitter}
              onChange={(e) => handleChange("twitter", e.target.value)}
              placeholder="https://twitter.com/yourhandle"
              className="bg-white/5 border-white/10 text-white placeholder:text-white/30"
            />
          </div>

          <div>
            <Label className="text-white/70 mb-2 block">Instagram Profile</Label>
            <Input
              value={formData.instagram}
              onChange={(e) => handleChange("instagram", e.target.value)}
              placeholder="https://instagram.com/yourhandle"
              className="bg-white/5 border-white/10 text-white placeholder:text-white/30"
            />
          </div>

          <div>
            <Label className="text-white/70 mb-2 block">Highlight Video URL</Label>
            <Input
              value={formData.highlight_video_url}
              onChange={(e) => handleChange("highlight_video_url", e.target.value)}
              placeholder="YouTube or other video link"
              className="bg-white/5 border-white/10 text-white placeholder:text-white/30"
            />
          </div>

          <div className="flex items-center gap-3 p-4 bg-white/5 rounded-xl">
            <input
              type="checkbox"
              id="allow_contact"
              checked={formData.allow_contact}
              onChange={(e) => handleChange("allow_contact", e.target.checked)}
              className="w-5 h-5 rounded border-white/20 bg-white/10 text-blue-800 focus:ring-blue-800"
            />
            <label htmlFor="allow_contact" className="text-white/70 text-sm">
              Allow coaches and scouts to contact me through NSB
            </label>
          </div>

          <div className="bg-green-500/10 border border-green-500/20 rounded-xl p-4">
            <p className="text-green-400 text-sm">
              ✅ Your profile will be visible to college coaches and scouts looking for talent like you!
            </p>
          </div>
        </div>
      )
    }
  ];

  const currentStepData = steps[step];
  const StepIcon = currentStepData.icon;

  return (
    <div className="min-h-screen bg-[#050508]" data-testid="create-profile-page">
      {/* Background Effects */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 left-1/4 w-[600px] h-[600px] bg-blue-900/20 rounded-full blur-[120px]" />
        <div className="absolute bottom-0 right-1/4 w-[500px] h-[500px] bg-blue-600/20 rounded-full blur-[100px]" />
      </div>

      {/* Header */}
      <header className="relative bg-black/30 backdrop-blur-xl border-b border-white/5">
        <div className="max-w-2xl mx-auto px-4 py-4 flex items-center justify-between">
          <NSBLogo className="h-10" />
          <span className="text-white/50 text-sm">Create Your Profile</span>
        </div>
      </header>

      {/* Main Content */}
      <div className="relative max-w-2xl mx-auto px-4 py-12">
        <StepIndicator currentStep={step} totalSteps={steps.length} />

        {/* Step Card */}
        <div className="bg-gradient-to-br from-white/[0.08] to-white/[0.02] backdrop-blur-xl rounded-3xl border border-white/10 overflow-hidden">
          {/* Step Header */}
          <div className="px-8 py-6 border-b border-white/10 flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-900 to-blue-600 flex items-center justify-center">
              <StepIcon className="h-6 w-6 text-white" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-white">{currentStepData.title}</h2>
              <p className="text-white/50 text-sm">{currentStepData.subtitle}</p>
            </div>
            <div className="ml-auto text-white/30 text-sm">
              Step {step + 1} of {steps.length}
            </div>
          </div>

          {/* Step Content */}
          <div className="p-8">
            {currentStepData.content}
          </div>

          {/* Navigation */}
          <div className="px-8 py-6 border-t border-white/10 flex items-center justify-between">
            {step > 0 ? (
              <Button
                variant="ghost"
                onClick={prevStep}
                className="text-white/70 hover:text-white hover:bg-white/10"
              >
                <ChevronLeft className="h-4 w-4 mr-2" />
                Back
              </Button>
            ) : (
              <div />
            )}

            {step < steps.length - 1 ? (
              <Button
                onClick={nextStep}
                className="bg-gradient-to-r from-blue-900 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white"
              >
                Continue
                <ChevronRight className="h-4 w-4 ml-2" />
              </Button>
            ) : (
              <Button
                onClick={handleSubmit}
                disabled={loading}
                className="bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white"
              >
                {loading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2" />
                    Creating...
                  </>
                ) : (
                  <>
                    <Check className="h-4 w-4 mr-2" />
                    Create Profile
                  </>
                )}
              </Button>
            )}
          </div>
        </div>

        {/* Skip Option */}
        {step < steps.length - 1 && (
          <p className="text-center mt-6 text-white/40 text-sm">
            You can always add more details later from your dashboard
          </p>
        )}
      </div>
    </div>
  );
}
